import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CustomInput from 'components/CustomInput';
import StoreBasicInfo from 'components/StoreBasicInfo';
import TradingHoursToday from 'components/TradingHoursToday';
import TextWithIcon from 'components/TextWithIcon';
import StoresCount from 'components/StoresCount';
import { setCurrentLocation, resetCurrentLocation } from 'data/CurrentLocation/actions';
import { hideListContent } from 'data/ListContent/actions';
import { setCenter, setZoom } from 'data/MapSettings/actions';
import { setSearchTerm, resetSearchTerm } from 'data/SearchTerm/actions';
import { getFilteredLocations } from 'services/selectors';
import getDayName from 'services/getDayName';
import ListNavigation from './components/ListNavigation';
import ListContent from './components/ListContent';
import ListItem from './components/ListItem';
import ListItemSimple from './components/ListItemSimple';
import { ReactComponent as AllStoresIcon } from './icon-all-stores.svg';
import { ReactComponent as MyLocationIcon } from './icon-my-location.svg';
import './index.scss';

/*
 * Types definition
 */
const propTypes = {
    listContent: PropTypes.bool,
    locations: PropTypes.objectOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            fullAddress: PropTypes.oneOfType([
                PropTypes.shape({
                    address: PropTypes.string,
                    lat: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                    ]),
                    lng: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                    ]),
                }),
                PropTypes.string,
            ]),
            tradingHours: PropTypes.shape({
                monday: PropTypes.string,
                tuesday: PropTypes.string,
                wednesday: PropTypes.string,
                thursday: PropTypes.string,
                friday: PropTypes.string,
                saturday: PropTypes.string,
                sunday: PropTypes.string,
            }),
        })
    ),
    userLocation: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
    }),
    searchTerm: PropTypes.string,
    onListItemClick: PropTypes.func,
    onSearchInput: PropTypes.func,
    onMyLocationClick: PropTypes.func,
    onAllStoresClick: PropTypes.func,
};

const defaultProps = {
    listContent: false,
    locations: null,
    userLocation: null,
    searchTerm: '',
    onListItemClick: null,
    onSearchInput: null,
    onMyLocationClick: null,
    onAllStoresClick: null,
};

/*
 * Component
 */
class List extends React.PureComponent {
    constructor(props) {
        super(props);

        this.handleListItemClick = this.handleListItemClick.bind(this);
        this.handleSearchInput = this.handleSearchInput.bind(this);
        this.handleMyLocationClick = this.handleMyLocationClick.bind(this);
        this.handleAllStoresClick = this.handleAllStoresClick.bind(this);
    }

    handleListItemClick(locationId) {
        return () => {
            const { onListItemClick, locations } = this.props;
            let yOffset = 0.06;

            if (window.matchMedia('(max-width: 767px)').matches) {
                yOffset = 0.03;
            }

            if (onListItemClick) {
                const currentLocationAddress = locations[locationId].fullAddress;

                if (currentLocationAddress) {
                    const latLng = {
                        lat: parseFloat(currentLocationAddress.lat) + yOffset,
                        lng: currentLocationAddress.lng,
                    };

                    onListItemClick(locationId, latLng);

                    return;
                }

                onListItemClick(locationId);
            }
        };
    }

    handleSearchInput(e) {
        const { onSearchInput } = this.props;

        if (onSearchInput) {
            onSearchInput(e.target.value);
        }
    }

    handleMyLocationClick() {
        const { onMyLocationClick, userLocation } = this.props;

        if (!userLocation) {
            alert('To use this feature you must enable the geolocation tracking in your browser');
        }

        if (onMyLocationClick) {
            onMyLocationClick(userLocation);
        }
    }

    handleAllStoresClick() {
        const { onAllStoresClick } = this.props;

        if (onAllStoresClick) {
            onAllStoresClick();
        }
    }

    render() {
        const { listContent, locations, searchTerm } = this.props;

        return (
            <section className="list">
                <ListNavigation />
                {listContent
                && (
                    <ListContent>
                        <CustomInput
                            placeholder="Store name or address"
                            onChange={this.handleSearchInput}
                            value={searchTerm}
                        />
                        <ListItemSimple>
                            <TextWithIcon
                                icon={MyLocationIcon}
                                text="My location"
                                callback={this.handleMyLocationClick}
                            />
                            <TextWithIcon
                                icon={AllStoresIcon}
                                text="All stores"
                                callback={this.handleAllStoresClick}
                            />
                            <StoresCount count={Object.keys(locations).length} />
                        </ListItemSimple>
                        {locations && Object.keys(locations).map((key) => {
                            const { tradingHours, headquarter } = locations[key];
                            const name = headquarter ? 'Acai Headquarter' : 'Acai Brothers';

                            return (
                                <ListItem
                                    key={locations[key].title}
                                    callback={this.handleListItemClick(key)}
                                >
                                    <StoreBasicInfo
                                        name={`${name} - ${locations[key].title}`}
                                        address={
                                            locations[key].fullAddress
                                            && locations[key].fullAddress.address
                                        }
                                    />
                                    {tradingHours
                                    && (
                                        <TradingHoursToday
                                            tradingHours={
                                                locations[key].tradingHours[getDayName()]
                                            }
                                        />
                                    )}
                                </ListItem>
                            );
                        })}
                    </ListContent>
                )}
            </section>
        );
    }
}

List.propTypes = propTypes;
List.defaultProps = defaultProps;

const mapDispatchToProps = dispatch => ({
    onListItemClick: (locationId, latLng) => {
        dispatch(setCurrentLocation(locationId));
        dispatch(hideListContent());
        dispatch(setCenter(latLng));
        dispatch(setZoom(12));
    },
    onSearchInput: text => dispatch(setSearchTerm(text)),
    onMyLocationClick: (latLng) => {
        dispatch(setCenter(latLng));
        dispatch(setZoom(12));
    },
    onAllStoresClick: () => {
        dispatch(resetCurrentLocation());
        dispatch(resetSearchTerm());
    }
});

const mapStateToProps = state => ({
    listContent: state.listContent,
    locations: getFilteredLocations(state),
    userLocation: state.userLocation,
    searchTerm: state.searchTerm,
});

export default connect(mapStateToProps, mapDispatchToProps)(List);
